import { jsx as _jsx } from "react/jsx-runtime";
import mapKeys from 'lodash/mapKeys';
import noop from 'lodash/noop';
import { createContext, useCallback, useContext, useEffect, useMemo, useState, } from 'react';
import { useList, useMap } from 'react-use';
import { analytics } from 'app/utils/segment';
import { useAppSelector } from 'utils/redux/useAppSelector';
import { isDateFilterField } from '../../utils/date/isDateFilterField';
import { isNullishFilterOperator } from '../../utils/filters/isNullishFilterOperator';
import { fieldOperators, filterFieldsPerTab, filtersInitialState, } from './utils/definitions';
/** The initial context value. */
export const initialContextValue = {
    tab: 'conversations',
    fields: filterFieldsPerTab['conversations'],
    strategy: filtersInitialState.strategy,
    conditions: filtersInitialState.conditions,
    validation: {
        errors: {},
        isEmpty: false,
        isValid: true,
        validate: () => false,
        clearErrors: noop,
    },
    addCondition: noop,
    updateCondition: noop,
    copyCondition: noop,
    removeCondition: noop,
    updateStrategy: noop,
};
/** The context. */
export const SearchFiltersContext = createContext(initialContextValue);
/** The context provider. All its children will have access to its value. */
export function SearchFiltersProvider({ tab, children, }) {
    const filters = useAppSelector((state) => state.search.params.filters);
    const [strategy, setStrategy] = useState(filters.strategy);
    const [conditions, conditionSetters] = useList(filters.conditions);
    const [errors, errorSetters] = useMap({});
    /**
     * Updates the strategy and conditions when the external state is updated,
     * e.g. when applying a saved search.
     */
    useEffect(() => {
        setStrategy(filters.strategy);
        conditionSetters.set(filters.conditions);
    }, [filters]);
    /** Sets the new strategy and sends a tracking event */
    const updateStrategy = (strategy) => {
        setStrategy(strategy);
        analytics.searchFilterStrategyUpdated({ strategy });
    };
    /**
     * Checks whether a condition is valid according to its field, operator and
     * values.
     *
     * @returns {string | null} The error message if invalid, or nothing if valid
     */
    const validateCondition = useCallback(({ field, operator, values, }) => {
        if (isDateFilterField(field)) {
            if (operator === 'between') {
                const isInvalid = !values.length ||
                    (values.length === 2 && values.some(({ value }) => !value));
                return isInvalid ? 'invalidRange' : null;
            }
            const isInvalid = !values.length;
            return isInvalid ? 'noDate' : null;
        }
        // For all other fields we just check that values are provided if the operator requires them.
        const isInvalid = !isNullishFilterOperator(operator) && !values.length;
        return isInvalid ? 'noValue' : null;
    }, []);
    /**
     * Validates each condition and if the validator returns an error, it adds it
     * to the errors state.
     *
     * @returns {boolean} `false` if at least one condition is invalid, otherwise
     *   `true`
     */
    const validateConditions = useCallback(() => {
        const invalidConditions = conditions.reduce((acc, condition, index) => {
            const errorKey = validateCondition(condition);
            if (errorKey)
                acc[index] = errorKey;
            return acc;
        }, {});
        const isValid = Object.keys(invalidConditions).length === 0;
        if (!isValid) {
            errorSetters.setAll(invalidConditions);
            analytics.searchFiltersValidationFailed({
                errors: mapKeys(invalidConditions, (_errorKey, index) => conditions[parseInt(index)].field),
            });
        }
        return isValid;
    }, [conditions, errorSetters, validateCondition]);
    /** Adds a new condition to the state with a default operator. */
    const addCondition = useCallback((field) => {
        const operator = fieldOperators[field].defaultOperator;
        conditionSetters.push({
            field,
            operator,
            values: [],
        });
        analytics.searchFilterConditionAdded({
            location: 'add_more',
            type: field,
        });
    }, [conditionSetters]);
    /**
     * Updates an existing condition in the state using its index value. If the
     * condition was previously invalid, and is now valid, the previous error is
     * cleared.
     */
    const updateCondition = useCallback((index, condition) => {
        conditionSetters.updateAt(index, condition);
        const isValid = !validateCondition(condition);
        if (isValid)
            errorSetters.remove(index);
    }, [conditionSetters, errorSetters, validateCondition]);
    /**
     * Creates a copy of an existing condition and inserts it immediately after
     * the condition being copied. If `options.fullCopy` is `false`, only the
     * `field` is copied. If `true`, the `operator` and `values` are copied as
     * well.
     */
    const copyCondition = useCallback((index, condition, options) => {
        const newCondition = Object.assign(Object.assign({}, condition), (!options.fullCopy
            ? {
                operator: fieldOperators[condition.field].defaultOperator,
                values: [],
            }
            : {}));
        conditionSetters.insertAt(index + 1, newCondition);
        analytics.searchFilterConditionAdded({
            location: options.fullCopy ? 'duplicate' : 'add_another',
            type: condition.field,
        });
    }, [conditionSetters]);
    /**
     * Removes a condition from the state by its index value and clears any
     * validation errors.
     */
    const removeCondition = useCallback((index) => {
        const { field } = conditions[index];
        analytics.searchFilterConditionRemoved({ type: field });
        conditionSetters.removeAt(index);
        errorSetters.remove(index);
    }, [conditions, conditionSetters, errorSetters]);
    /**
     * A memoized list of fields that are available to filter, according to the
     * search page `tab`.
     */
    const fields = useMemo(() => filterFieldsPerTab[tab], [tab]);
    const contextValue = {
        tab,
        fields,
        strategy,
        conditions,
        validation: {
            errors,
            isEmpty: !conditions.length,
            isValid: !Object.keys(errors).length,
            validate: validateConditions,
            clearErrors: errorSetters.reset,
        },
        addCondition,
        updateCondition,
        copyCondition,
        removeCondition,
        updateStrategy,
    };
    return (_jsx(SearchFiltersContext.Provider, Object.assign({ value: contextValue }, { children: children })));
}
/** A hook exposing the context functionality. */
export function useSearchFilters() {
    return useContext(SearchFiltersContext);
}
